import { RootState } from "../../index";
import { createSelector } from "@reduxjs/toolkit";
import { EMPTY_ARRAY, EMPTY_OBJECT } from "Utils";
import { UNASSIGNED_MISSIONS_KEY } from "Slices/missionsPage/index";

export const getLastUpdateAt = (state: RootState) =>
  state.missionsPage.lastUpdateAt;
export const getMissionsPageFleetId = (state: RootState) =>
  state.missionsPage.missionsPageFleetId;
export const getMissions = (state: RootState) => state.missionsPage.missions;

// Mirrors cloud-core QueuedMission.ACTIVE_STATUSES — statuses where a mission
// is occupying a vehicle (BLOCKED_BY_MANUAL = waiting for HMI release).
const ACTIVE_MISSION_STATUSES = ["IN_PROGRESS", "PAUSED", "BLOCKED_BY_MANUAL"];
export const getActiveMissionByVehicleId = createSelector(
  getMissions,
  (missions) => {
    if (!missions) {
      return EMPTY_OBJECT;
    }
    const result = missions.reduce((acc: Record<string, Mission>, mission) => {
      if (ACTIVE_MISSION_STATUSES.includes((mission as any).status)) {
        const vehicleId = (mission as any).vehicle;
        if (vehicleId && !acc[vehicleId]) {
          acc[vehicleId] = mission;
        }
      }
      return acc;
    }, {});
    return result;
  }
);

const addMissionByVehicle = (
  store: Record<string, Mission[]>,
  vehicleId: string,
  mission: Mission
) => {
  if (store[vehicleId]) {
    store[vehicleId] = store[vehicleId].concat(mission);
  } else {
    store[vehicleId] = [mission];
  }
};

export const getQueueManagerMissionsData = createSelector(
  getMissions,
  (missions) => {
    if (!missions) {
      return EMPTY_OBJECT;
    }
    const result = missions.reduce(
      (acc: Record<string, Mission[]>, mission) => {
        const status = (mission as any).status;
        const vehicleId = (mission as any).vehicle;
        if (["NEW", "SENT", "RECEIVED", "IN_QUEUE"].includes(status)) {
          addMissionByVehicle(
            acc,
            vehicleId ?? UNASSIGNED_MISSIONS_KEY,
            mission
          );
        } else if (ACTIVE_MISSION_STATUSES.includes(status) && vehicleId) {
          // Keep the vehicle's section mounted while it runs a mission,
          // even if nothing else is queued for it.
          acc[vehicleId] = acc[vehicleId] ?? [];
        }
        return acc;
      },
      {}
    );
    return result;
  }
);

export const getLiveMissionsData = createSelector(getMissions, (missions) => {
  if (!missions) {
    return EMPTY_ARRAY;
  }
  // A mission is "live" for every status where it still occupies a vehicle —
  // the same ACTIVE_MISSION_STATUSES the queue/station views use and that
  // cloud-core defines as QueuedMission.ACTIVE_STATUSES. BLOCKED_BY_MANUAL
  // (parked awaiting HMI release) is still executing, so it must stay on the
  // live board instead of vanishing the moment the walker reaches the stop.
  const result = missions.filter((mission) =>
    ACTIVE_MISSION_STATUSES.includes((mission as any).status)
  );
  return result;
});

export const getMissionById = (missionId: string | undefined) =>
  createSelector(getMissions, (missions) => {
    if (!missions || !missionId) return;
    return missions.find((mission) => mission.id === missionId);
  });

export const getIsMissionsUpdatesPaused = (state: RootState) =>
  state.missionsPage.isMissionsUpdatesPaused;
export const getMissionsUpdatesStorage = (state: RootState) =>
  state.missionsPage.missionsUpdatesStorage;
